"use client";

import { CribNoshLogo } from "@/components/ui/CribNoshLogo";
import { api } from "@/convex/_generated/api";
import { FoodCreatorLoadingScreen } from "@/components/food-creator/FoodCreatorAuthGuard";
import type { Id } from "@/convex/_generated/dataModel";
import { hasFoodCreatorWebAccess } from "@/lib/auth/access";
import { normalizeOtpError } from "@/lib/auth/otp-error";
import { clearAuthToken, setAuthToken } from "@/lib/auth-client";
import { useWebAuth } from "@/lib/web-auth";
import { getDeviceInfo } from "@/lib/utils/device";
import { useAction, useConvex, useQuery } from "convex/react";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Loader2, Mail, X } from "lucide-react";
import { AuthLegalNotice } from "./auth-legal-notice";
import { EmailSignInModal } from "./email-sign-in-modal";
import { SocialSignIn } from "./social-sign-in";

type Persona = "food_creator" | "driver" | "advertiser";

type PersonaSignInPageProps = {
  persona: Persona;
  sessionStateOverride?: {
    user?: {
      _id?: string;
      roles?: string[];
      [key: string]: unknown;
    } | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    sessionToken: string | null;
  };
  onAuthenticated?: (
    sessionToken: string,
    user: {
      user_id: string;
      email: string;
      name: string;
      roles: string[];
      picture?: string;
      provider?: string;
      isNewUser?: boolean;
    },
  ) => Promise<void> | void;
};

type PersonaConfig = {
  allowedReturnPrefixes: readonly string[];
  backgroundSettingKey:
    | "web_food_creator_signin_background_image_url"
    | "web_signin_background_image_url";
  defaultBackgroundImage: string;
  emailPlaceholder: string;
  appLabel: string;
  choiceHeading: string;
  choiceSubtitle: string;
  heading: string;
  otpHeading: string;
  subtitle: string;
  emailActionLabel: string;
  emailActionStyle: "inline" | "block";
};

const OTP_LENGTH = 6;
const DEFAULT_FOOD_CREATOR_SIGNIN_BACKGROUND =
  "/assets/images/signin-background.jpg";
const DEFAULT_DRIVER_SIGNIN_BACKGROUND =
  "/auth/cribnosh-driver-signin-background.png";
const DEFAULT_ADVERTISER_SIGNIN_BACKGROUND =
  "/auth/cribnosh-driver-signin-background.png";
const GUARDED_APPLY_ROUTES = new Set([
  "/become-a-food-creator/apply",
  "/driving/apply",
]);

interface GoogleTokenResponse {
  access_token?: string;
  error?: string;
  error_description?: string;
}

interface GoogleTokenClient {
  requestAccessToken: () => void;
}

interface GoogleIdentityServices {
  accounts?: {
    oauth2?: {
      initTokenClient?: (config: {
        client_id: string;
        scope: string;
        callback: (response: GoogleTokenResponse) => void | Promise<void>;
      }) => GoogleTokenClient;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

const resolveBackgroundImageSrc = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("/")) return trimmed;

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
};

const resolveGoogleWebClientId = (
  settings: Record<string, unknown> | undefined,
) => {
  const runtimeValue =
    typeof settings?.auth_mobile_google_web_client_id === "string"
      ? settings.auth_mobile_google_web_client_id.trim()
      : "";
  const envValue = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID?.trim() || "";
  return runtimeValue || envValue;
};

const normalizeInternalReturnTo = (
  rawReturnTo: string | null | undefined,
  allowedPrefixes: readonly string[],
): string | null => {
  if (!rawReturnTo) return null;
  if (!rawReturnTo.startsWith("/")) return null;
  if (rawReturnTo.startsWith("//")) return null;
  if (!allowedPrefixes.some((prefix) => rawReturnTo.startsWith(prefix))) {
    return null;
  }
  return rawReturnTo;
};

const getPersonaConfig = (persona: Persona): PersonaConfig => {
  if (persona === "food_creator") {
    return {
      allowedReturnPrefixes: [
        "/food-creator",
        "/become-a-food-creator",
      ] as const,
      backgroundSettingKey:
        "web_food_creator_signin_background_image_url" as const,
      defaultBackgroundImage: DEFAULT_FOOD_CREATOR_SIGNIN_BACKGROUND,
      emailPlaceholder: "food.creator@example.com",
      appLabel: "Creator App",
      choiceHeading: "Ready?\nSet, Eat",
      choiceSubtitle:
        "Welcome. Let's start by creating your account or sign in if you already have one.",
      heading: "Continue as a Food Creator",
      otpHeading: "Enter your code",
      subtitle:
        "Enter your email to continue. New here? We'll set up your account.",
      emailActionLabel: "Sign in with Email",
      emailActionStyle: "inline" as const,
    };
  }

  if (persona === "advertiser") {
    return {
      allowedReturnPrefixes: ["/ads"] as const,
      backgroundSettingKey: "web_signin_background_image_url" as const,
      defaultBackgroundImage: DEFAULT_ADVERTISER_SIGNIN_BACKGROUND,
      emailPlaceholder: "ads@yourbrand.com",
      appLabel: "Ads Manager",
      choiceHeading: "Ready?\nSet, Launch",
      choiceSubtitle:
        "Sign in to build campaigns, plan placements, and get your advertiser workspace ready on CribNosh.",
      heading: "Continue to Ads Manager",
      otpHeading: "Enter your code",
      subtitle:
        "Enter your work email to continue. New here? We'll set up your advertiser account.",
      emailActionLabel: "Sign in with Email",
      emailActionStyle: "inline" as const,
    };
  }

  return {
    allowedReturnPrefixes: ["/driving"] as const,
    backgroundSettingKey: "web_signin_background_image_url" as const,
    defaultBackgroundImage: DEFAULT_DRIVER_SIGNIN_BACKGROUND,
    emailPlaceholder: "driver@example.com",
    appLabel: "Driver App",
    choiceHeading: "Ready?\nSet, Drive",
    choiceSubtitle:
      "Sign in to accept deliveries, manage your route, and track your earnings with CribNosh Driver.",
    heading: "Continue as a Driver",
    otpHeading: "Enter your code",
    subtitle:
      "Enter your email to continue. New here? We'll set up your account.",
    emailActionLabel: "Sign in with Email",
    emailActionStyle: "inline" as const,
  };
};

const resolveAuthSourceApp = (
  persona: Persona,
): "food_creator" | "driver" | "mobile" => {
  if (persona === "food_creator") return "food_creator";
  if (persona === "driver") return "driver";
  return "mobile";
};

function LoadingState({ label }: { label: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#02120A]">
      <div className="text-center">
        <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-[#4ADE80] border-t-transparent" />
        <p className="text-gray-400">{label}</p>
      </div>
    </div>
  );
}

function PersonaLoadingState({
  persona,
  label,
}: {
  persona: Persona;
  label: string;
}) {
  if (persona === "food_creator") {
    return <FoodCreatorLoadingScreen label={label} />;
  }

  return <LoadingState label={label} />;
}

export function PersonaSignInPage({
  persona,
  sessionStateOverride,
  onAuthenticated,
}: PersonaSignInPageProps) {
  const config = getPersonaConfig(persona);
  const sessionState = useWebAuth();
  const {
    user,
    isAuthenticated,
    isLoading,
    sessionToken,
  } = sessionStateOverride ?? sessionState;
  const router = useRouter();
  const searchParams = useSearchParams();
  const convex = useConvex();

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [step, setStep] = useState<"choice" | "email" | "otp">("choice");
  const [error, setError] = useState("");
  const [testOtp, setTestOtp] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(300);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isResolvingDestination, setIsResolvingDestination] = useState(false);
  const [isAppleSignInLoading, setIsAppleSignInLoading] = useState(false);
  const [isGoogleSignInLoading, setIsGoogleSignInLoading] = useState(false);
  const [isEmailSignInModalVisible, setIsEmailSignInModalVisible] =
    useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  const sendEmailOTP = useAction(api.actions.users.customerEmailSendOTP);
  const verifyAndLogin = useAction(
    api.actions.users.customerEmailVerifyAndLogin,
  );
  const googleAuth = useAction(api.actions.auth.googleAuth);
  const runtimeSettings = useQuery(api.queries.admin.getSystemSettings, {});
  const notDismissable = searchParams?.get("notDismissable") === "true";

  const returnTo = useMemo(
    () =>
      normalizeInternalReturnTo(
        searchParams?.get("returnTo"),
        config.allowedReturnPrefixes,
      ),
    [config.allowedReturnPrefixes, searchParams],
  );

  const backgroundImageSrc =
    resolveBackgroundImageSrc(runtimeSettings?.[config.backgroundSettingKey]) ??
    config.defaultBackgroundImage;
  const googleClientId = resolveGoogleWebClientId(runtimeSettings);
  const fallbackBackHref =
    persona === "food_creator"
      ? "/become-a-food-creator"
      : persona === "driver"
        ? "/driving"
        : "/ads";
  const handleBack = useCallback(() => {
    if (typeof window === "undefined") {
      router.push(fallbackBackHref);
      return;
    }

    const currentPath = `${window.location.pathname}${window.location.search}`;
    const referrer = document.referrer;

    if (referrer) {
      try {
        const previousUrl = new URL(referrer);
        const previousPath = `${previousUrl.pathname}${previousUrl.search}`;
        const isSameOrigin = previousUrl.origin === window.location.origin;
        const isGuardedApplyRoute = GUARDED_APPLY_ROUTES.has(previousUrl.pathname);

        if (
          isSameOrigin &&
          previousPath !== currentPath &&
          !isGuardedApplyRoute
        ) {
          router.back();
          return;
        }
      } catch {
        // Fall back to the public landing page below.
      }
    }

    router.push(fallbackBackHref);
  }, [fallbackBackHref, router]);

  const handleDismiss = useCallback(() => {
    if (notDismissable) {
      router.push(fallbackBackHref);
      return;
    }

    handleBack();
  }, [fallbackBackHref, handleBack, notDismissable, router]);

  const resolveDestination = useCallback(
    async (nextSessionToken: string, userId: Id<"users">): Promise<string> => {
      if (persona === "food_creator") {
        const foodCreator = await convex.query(
          api.queries.foodCreators.getByUserId,
          {
            userId,
            sessionToken: nextSessionToken,
          },
        );

        if (!foodCreator) {
          return returnTo?.startsWith("/become-a-food-creator")
            ? returnTo
            : "/become-a-food-creator/apply";
        }

        const basicOnboardingComplete = await convex.query(
          api.queries.foodCreators.isBasicOnboardingComplete,
          {
            foodCreatorId: foodCreator._id,
            sessionToken: nextSessionToken,
          },
        );

        if (!basicOnboardingComplete) {
          return returnTo?.startsWith("/food-creator/profile")
            ? returnTo
            : "/food-creator/profile";
        }

        const onboardingComplete = await convex.query(
          api.queries.foodCreatorCourses.isOnboardingComplete,
          {
            foodCreatorId: foodCreator._id,
            sessionToken: nextSessionToken,
          },
        );

        if (
          !onboardingComplete &&
          foodCreator.complianceTrainingSkipped !== true
        ) {
          return returnTo?.startsWith("/food-creator/onboarding")
            ? returnTo
            : "/food-creator/onboarding/course";
        }

        return returnTo ?? "/food-creator/dashboard";
      }

      if (persona === "advertiser") {
        try {
          const onboardingComplete =
            window.localStorage.getItem(
              "cribnosh_ads_manager_onboarding_complete",
            ) === "true";

          if (!onboardingComplete) {
            return returnTo?.startsWith("/ads/onboarding")
              ? returnTo
              : "/ads/onboarding";
          }

          return returnTo ?? "/ads/dashboard";
        } catch {
          return returnTo?.startsWith("/ads/onboarding")
            ? returnTo
            : "/ads/onboarding";
        }
      }

      const driver = await convex.query(api.queries.drivers.getByUserId, {
        userId,
        sessionToken: nextSessionToken,
      });

      if (!driver) {
        return returnTo?.startsWith("/driving/apply")
          ? returnTo
          : "/driving/apply";
      }

      return returnTo ?? "/driving/app";
    },
    [convex, persona, returnTo],
  );

  useEffect(() => {
    if (step !== "otp" || timeLeft <= 0) return;
    const timer = window.setTimeout(
      () => setTimeLeft((current) => current - 1),
      1000,
    );
    return () => window.clearTimeout(timer);
  }, [step, timeLeft]);

  useEffect(() => {
    if (step !== "otp") return;
    const timer = window.setTimeout(() => inputRefs.current[0]?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [step]);

  useEffect(() => {
    if (step !== "email") return;
    const timer = window.setTimeout(() => emailInputRef.current?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [step]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    document.body.classList.add("hide-site-header");
    return () => {
      document.body.classList.remove("hide-site-header");
    };
  }, []);

  useEffect(() => {
    if (
      persona === "food_creator" &&
      !isLoading &&
      isAuthenticated &&
      sessionToken &&
      user &&
      !hasFoodCreatorWebAccess(user)
    ) {
      clearAuthToken();
      window.location.replace(window.location.pathname + window.location.search);
      return;
    }

    if (isLoading || !isAuthenticated || !sessionToken || !user?._id) {
      return;
    }

    let cancelled = false;
    setIsResolvingDestination(true);
    setError("");

    void resolveDestination(sessionToken, user._id as Id<"users">)
      .then((destination) => {
        if (!cancelled) {
          router.replace(destination);
        }
      })
      .catch((destinationError) => {
        if (!cancelled) {
          setError(
            normalizeOtpError(
              destinationError,
              "We could not finish sign-in. Please try again.",
            ).message,
          );
          setIsResolvingDestination(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    persona,
    isAuthenticated,
    isLoading,
    resolveDestination,
    router,
    sessionToken,
    user?._id,
    user,
  ]);

  const handleSendCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setIsSendingCode(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(normalizedEmail)) {
        setError("Enter a valid email address.");
        setIsSendingCode(false);
        return;
      }

      const result = await sendEmailOTP({
        email: normalizedEmail,
        sourceApp: resolveAuthSourceApp(persona),
      });

      if (result.success) {
        setEmail(normalizedEmail);
        setOtp(Array(OTP_LENGTH).fill(""));
        setTestOtp(result.testOtp ?? null);
        setTimeLeft(300);
        setStep("otp");
      } else {
        setError(
          normalizeOtpError(
            result.error,
            "We could not send a verification code.",
          ).message,
        );
      }
    } catch (sendError) {
      setError(
        normalizeOtpError(sendError, "We could not send a verification code.")
          .message,
      );
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async (event: React.FormEvent) => {
    event.preventDefault();
    const otpString = otp.join("");
    if (otpString.length !== OTP_LENGTH) {
      setError("Enter the full 6-digit code.");
      return;
    }

    setError("");
    setIsVerifyingCode(true);

    try {
      const result = await verifyAndLogin({
        email,
        otp: otpString,
        sourceApp: resolveAuthSourceApp(persona),
        userAgent: window.navigator.userAgent,
        ...getDeviceInfo(),
      });

      if (result.success && result.sessionToken) {
        const nextSessionToken = result.sessionToken;
        const userId = result.user.user_id as Id<"users">;
        await setAuthToken(nextSessionToken);
        await onAuthenticated?.(nextSessionToken, result.user);
        const destination = await resolveDestination(nextSessionToken, userId);
        router.replace(destination);
        return;
      }

      if ("requires2FA" in result && result.requires2FA) {
        setError(
          "Two-factor verification is required. Please complete sign-in on mobile or contact support.",
        );
        return;
      }

      setError(
        normalizeOtpError(
          "error" in result ? result.error : result,
          "Verification failed. Please try again.",
        ).message,
      );
    } catch (verifyError) {
      setError(
        normalizeOtpError(verifyError, "Verification failed. Please try again.")
          .message,
      );
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, OTP_LENGTH);
    const nextOtp = [...otp];

    if (digits.length > 1) {
      const startIndex = digits.length === OTP_LENGTH ? 0 : index;
      digits.split("").forEach((digit, offset) => {
        const targetIndex = startIndex + offset;
        if (targetIndex < nextOtp.length) {
          nextOtp[targetIndex] = digit;
        }
      });
      setOtp(nextOtp);
      setError("");
      inputRefs.current[
        Math.min(startIndex + digits.length - 1, nextOtp.length - 1)
      ]?.focus();
      return;
    }

    nextOtp[index] = digits;
    setOtp(nextOtp);
    if (digits) {
      setError("");
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (
    index: number,
    event: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (pasted) {
      handleOtpChange(index, pasted);
    }
  };

  const handleResendCode = async () => {
    if (isSendingCode) return;
    setError("");
    setIsSendingCode(true);
    try {
      const result = await sendEmailOTP({
        email,
        sourceApp: resolveAuthSourceApp(persona),
      });
      if (result.success) {
        setOtp(Array(OTP_LENGTH).fill(""));
        setTestOtp(result.testOtp ?? null);
        setTimeLeft(300);
        inputRefs.current[0]?.focus();
      } else {
        setError(
          normalizeOtpError(result.error, "We could not resend the code.")
            .message,
        );
      }
    } catch (resendError) {
      setError(
        normalizeOtpError(resendError, "We could not resend the code.").message,
      );
    } finally {
      setIsSendingCode(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleShowEmailStep = useCallback(() => {
    setError("");
    setTestOtp(null);
    setIsEmailSignInModalVisible(true);
  }, []);

  const handleReturnToOptions = useCallback(() => {
    setError("");
    setOtp(Array(OTP_LENGTH).fill(""));
    setTestOtp(null);
    setTimeLeft(300);
    setStep("choice");
  }, []);

  const handleGoogleAuthResult = useCallback(
    async (accessToken: string) => {
      const data = await googleAuth({
        accessToken,
        userAgent: navigator.userAgent,
        ...getDeviceInfo(),
      });

      if (data.success && data.sessionToken) {
        const nextSessionToken = data.sessionToken;
        const userId = data.user.user_id as Id<"users">;
        await setAuthToken(nextSessionToken);
        await onAuthenticated?.(nextSessionToken, data.user);
        const destination = await resolveDestination(nextSessionToken, userId);
        router.replace(destination);
        return;
      }

      if ("requires2FA" in data && data.requires2FA) {
        throw new Error(
          "Two-factor verification is required. Please complete sign-in on mobile or contact support.",
        );
      }

      throw new Error(
        "error" in data && data.error
          ? data.error
          : "Google sign-in failed. Please try again.",
      );
    },
    [googleAuth, onAuthenticated, resolveDestination, router],
  );

  const initializeGoogleSignIn = useCallback(
    (googleClientId: string) => {
      let attempts = 0;
      const maxAttempts = 50;

      const checkGoogle = window.setInterval(() => {
        attempts += 1;

        if (window.google) {
          window.clearInterval(checkGoogle);

          try {
            const initTokenClient = window.google.accounts?.oauth2?.initTokenClient;
            if (!initTokenClient) {
              throw new Error("Google sign-in is unavailable right now.");
            }

            const client = initTokenClient({
              client_id: googleClientId,
              scope: "openid profile email",
              callback: async (response) => {
                try {
                  if (response.error || !response.access_token) {
                    throw new Error(
                      response.error_description ||
                        "Google sign-in was cancelled or failed.",
                    );
                  }

                  await handleGoogleAuthResult(response.access_token);
                } catch (googleError) {
                  setError(
                    normalizeOtpError(
                      googleError,
                      "Google sign-in failed. Please try again.",
                    ).message,
                  );
                } finally {
                  setIsGoogleSignInLoading(false);
                }
              },
            });

            client.requestAccessToken();
          } catch (googleError) {
            setError(
              normalizeOtpError(
                googleError,
                "Failed to initialize Google sign-in.",
              ).message,
            );
            setIsGoogleSignInLoading(false);
          }

          return;
        }

        if (attempts >= maxAttempts) {
          window.clearInterval(checkGoogle);
          setError("Google sign-in is unavailable right now. Please try again.");
          setIsGoogleSignInLoading(false);
        }
      }, 100);
    },
    [handleGoogleAuthResult],
  );

  const handleGoogleSignIn = useCallback(async () => {
    if (isGoogleSignInLoading) return;

    setError("");
    setIsGoogleSignInLoading(true);

    try {
      if (!googleClientId) {
        throw new Error(
          "Google sign-in is not configured right now. Continue with email instead.",
        );
      }

      if (!window.google) {
        const existingScript = document.querySelector<HTMLScriptElement>(
          'script[src="https://accounts.google.com/gsi/client"]',
        );

        if (!existingScript) {
          const script = document.createElement("script");
          script.src = "https://accounts.google.com/gsi/client";
          script.async = true;
          script.defer = true;
          script.onload = () => initializeGoogleSignIn(googleClientId);
          script.onerror = () => {
            setError("Failed to load Google sign-in. Please try again.");
            setIsGoogleSignInLoading(false);
          };
          document.head.appendChild(script);
          return;
        }
      }

      initializeGoogleSignIn(googleClientId);
    } catch (googleError) {
      setError(
        normalizeOtpError(
          googleError,
          "Google sign-in failed. Please try again.",
        ).message,
      );
      setIsGoogleSignInLoading(false);
    }
  }, [googleClientId, initializeGoogleSignIn, isGoogleSignInLoading]);

  const handleAppleSignIn = useCallback(() => {
    if (isAppleSignInLoading) return;

    setError("");
    setIsAppleSignInLoading(true);

    try {
      const redirectPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const startUrl = new URL("/api/auth/apple/start", window.location.origin);
      startUrl.searchParams.set("redirect", redirectPath);
      window.location.assign(startUrl.toString());
    } catch (appleError) {
      setError(
        normalizeOtpError(
          appleError,
          "Apple sign-in failed. Please try again.",
        ).message,
      );
      setIsAppleSignInLoading(false);
    }
  }, [isAppleSignInLoading]);

  const appCta =
    persona === "food_creator"
      ? {
          href: "/food-creator",
          iconSrc: "/app-downloads/creator.png",
          label: "Continue in Food Creator App",
        }
      : persona === "driver"
        ? {
            href: "/driving/app",
            iconSrc: "/app-downloads/driver.png",
            label: "Continue in Driver App",
        }
      : null;

  const shouldShowBlockingLoader = isLoading || isResolvingDestination;

  if (shouldShowBlockingLoader) {
    return (
      <PersonaLoadingState
        persona={persona}
        label={isResolvingDestination ? "Loading..." : "Checking your session..."}
      />
    );
  }

  return (
    <>
      <div className="relative min-h-screen overflow-hidden bg-[#02120A]">
      <div
        className="absolute inset-0 z-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('${backgroundImageSrc}')`,
          filter: "brightness(0.5)",
        }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,18,10,0.1)_0%,rgba(2,18,10,0.35)_42%,rgba(2,18,10,0.92)_100%)]" />

      {!notDismissable ? (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Close sign in"
          className="fixed right-4 top-5 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/30 text-white shadow-lg backdrop-blur transition hover:bg-black/40 sm:right-6 sm:top-6"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      ) : null}

      <div className="relative z-10 flex min-h-screen flex-col">
        <div className="px-6 pb-8 pt-16 sm:px-8 sm:pt-20">
          <div className="flex max-w-[18rem] flex-col items-start">
            <CribNoshLogo
              size={172}
              variant={persona === "food_creator" ? "default" : "white"}
            />
            <p className="mt-2 pl-1 text-sm font-bold uppercase tracking-[0.24em] text-white">
              {config.appLabel}
            </p>
          </div>
        </div>

        <div className="mt-auto w-full md:flex md:justify-center">
          <div className="w-full rounded-t-[32px] bg-[rgba(2,18,10,0.98)] px-6 pb-[calc(2rem+env(safe-area-inset-bottom,0px))] pt-9 shadow-[0_-20px_60px_rgba(0,0,0,0.35)] md:max-w-[32rem] md:rounded-t-[34px] md:px-7">
            {step === "choice" ? (
              <>
                <div
                  className={
                    config.emailActionStyle === "inline"
                      ? "mb-4 flex items-start justify-between gap-4"
                      : "mb-4"
                  }
                >
                  <h1 className="whitespace-pre-line text-[2.125rem] font-bold leading-[1.08] tracking-[-0.03em] text-white">
                    {config.choiceHeading}
                  </h1>

                  {config.emailActionStyle === "inline" ? (
                    <button
                      type="button"
                      onClick={handleShowEmailStep}
                      className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-sm font-medium text-white/80 transition hover:text-white"
                    >
                      <span>{config.emailActionLabel}</span>
                      <ArrowRight className="h-4 w-4 text-[#4ADE80]" />
                    </button>
                  ) : null}
                </div>

                <div className="mb-4 h-0.5 w-8 rounded-full bg-[#4ADE80]" />

                <p className="mb-8 max-w-[25rem] text-[1.04rem] leading-7 text-[#F8FAFC]">
                  {config.choiceSubtitle}
                </p>

                {error ? (
                  <div className="mb-5 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm font-medium text-red-300">
                    {error}
                  </div>
                ) : null}

                {config.emailActionStyle === "block" ? (
                  <button
                    type="button"
                    onClick={handleShowEmailStep}
                    className="mb-4 inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-[#FF3B30] px-5 py-4 text-base font-bold text-white shadow-[0_10px_30px_rgba(255,59,48,0.25)] transition hover:bg-[#ff554a]"
                  >
                    <Mail className="h-5 w-5" aria-hidden="true" />
                    <span>{config.emailActionLabel}</span>
                  </button>
                ) : null}

                <SocialSignIn
                  onGoogleSignIn={() => void handleGoogleSignIn()}
                  onAppleSignIn={handleAppleSignIn}
                  isAppleSignInAvailable
                  isAppleSignInLoading={isAppleSignInLoading}
                  isGoogleSignInLoading={isGoogleSignInLoading}
                />

                <div className="mt-5">
                  <AuthLegalNotice />
                </div>

                {appCta ? (
                  <button
                    type="button"
                    onClick={() => router.push(appCta.href)}
                    className="mt-4 inline-flex w-full items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition hover:border-white/20 hover:bg-white/10"
                  >
                    <span className="relative h-9 w-9 overflow-hidden rounded-[10px] ring-1 ring-white/10">
                      <Image
                        src={appCta.iconSrc}
                        alt=""
                        fill
                        aria-hidden="true"
                        className="object-cover"
                        sizes="36px"
                      />
                    </span>
                    <span>{appCta.label}</span>
                  </button>
                ) : null}
              </>
            ) : null}

            {step !== "choice" ? (
              <>
                <div className="mb-6 flex items-start justify-between gap-4">
                  <div>
                    <h1 className="text-[1.72rem] font-bold leading-tight text-white">
                      {step === "email" ? config.heading : config.otpHeading}
                    </h1>
                    <p className="mt-2 max-w-[24rem] text-sm leading-6 text-gray-300">
                      {step === "email"
                        ? config.subtitle
                        : "Enter the 6-digit code we sent to your email."}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleReturnToOptions}
                    className="shrink-0 rounded-full border border-white/10 px-3 py-1.5 text-xs font-semibold text-white/75 transition hover:border-white/20 hover:text-white"
                  >
                    Other options
                  </button>
                </div>

                <form
                  className="space-y-4"
                  onSubmit={step === "email" ? handleSendCode : handleVerifyCode}
                >
                  {error ? (
                    <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm font-medium text-red-300">
                      {error}
                    </div>
                  ) : null}

                  <div>
                    <label
                      htmlFor="email"
                      className="mb-1.5 block text-sm font-bold text-gray-200"
                    >
                      Email address
                    </label>
                    <input
                      ref={emailInputRef}
                      type="email"
                      id="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition-all placeholder:text-gray-500 focus:border-[#4ADE80] focus:ring-2 focus:ring-[#4ADE80]/30"
                      placeholder={config.emailPlaceholder}
                      required
                      disabled={step === "otp"}
                    />
                  </div>

                  {step === "otp" ? (
                    <>
                      <div>
                        <div className="mb-1.5 flex items-center justify-between">
                          <label className="block text-sm font-bold text-gray-200">
                            Verification code
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              setStep("email");
                              setOtp(Array(OTP_LENGTH).fill(""));
                              setError("");
                            }}
                            className="text-xs font-medium text-[#4ADE80] hover:text-[#7ef0a1]"
                          >
                            Use another email
                          </button>
                        </div>
                        <div className="flex gap-2">
                          {otp.map((digit, index) => (
                            <input
                              key={index}
                              ref={(element) => {
                                inputRefs.current[index] = element;
                              }}
                              inputMode="numeric"
                              autoComplete={index === 0 ? "one-time-code" : "off"}
                              maxLength={OTP_LENGTH}
                              value={digit}
                              onChange={(event) =>
                                handleOtpChange(index, event.target.value)
                              }
                              onKeyDown={(event) =>
                                handleOtpKeyDown(index, event)
                              }
                              onPaste={(event) => handleOtpPaste(index, event)}
                              className="h-12 w-12 rounded-2xl border border-white/10 bg-white/5 text-center text-lg font-bold text-white outline-none transition-all focus:border-[#4ADE80] focus:ring-2 focus:ring-[#4ADE80]/30"
                            />
                          ))}
                        </div>
                        <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
                          <span>Code expires in {formatTime(timeLeft)}</span>
                          <button
                            type="button"
                            onClick={() => void handleResendCode()}
                            disabled={isSendingCode}
                            className="font-medium text-[#4ADE80] hover:text-[#7ef0a1] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Resend code
                          </button>
                        </div>
                        {testOtp ? (
                          <p className="mt-3 rounded-2xl border border-[#4ADE80]/25 bg-[#4ADE80]/10 px-3 py-2 text-xs font-medium text-[#b6f6c8]">
                            Dev mode code:{" "}
                            <span className="font-mono text-white">{testOtp}</span>
                          </p>
                        ) : null}
                      </div>

                      <button
                        type="submit"
                        disabled={isVerifyingCode}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#FF3B30] px-6 py-3.5 font-bold text-white shadow-lg transition hover:bg-[#ff554a] disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        {isVerifyingCode ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Checking code...</span>
                          </>
                        ) : (
                          "Continue"
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSendingCode}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#FF3B30] px-6 py-3.5 font-bold text-white shadow-lg transition hover:bg-[#ff554a] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {isSendingCode ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Sending code...</span>
                        </>
                      ) : (
                        "Send code"
                      )}
                    </button>
                  )}
                </form>
              </>
            ) : null}
          </div>
        </div>
      </div>
      </div>

      <EmailSignInModal
        isVisible={isEmailSignInModalVisible}
        onClose={() => setIsEmailSignInModalVisible(false)}
        sourceApp={resolveAuthSourceApp(persona)}
        title={config.heading}
        subtitle={config.subtitle}
        emailPlaceholder={config.emailPlaceholder}
        continueLabel="Send verification code"
      />
    </>
  );
}
